import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { FlatList, StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { FileUpload } from '@shared/components/blocks/types';
import { Pill } from '@shared/components/pill';
import { isImage } from '@shared/util/media/type';
import { FileItem } from './FileItem';

interface FilesProps {
  files: FileUpload[];
  maxFiles?: number;
  editable: boolean;
  showFileIcon?: boolean;
  onFileRemoved?: (file: FileUpload) => void;
  style?: StyleProp<ViewStyle>;
  itemStyle?: StyleProp<ViewStyle>;
}

export function FileList({
  files,
  maxFiles = Infinity,
  onFileRemoved,
  editable,
  showFileIcon = true,
  style,
  itemStyle,
}: FilesProps) {
  const nonImages = useMemo(() => files.filter(file => !isImage(file)), [files]);
  const filesToShow = useMemo(() => nonImages.slice(0, maxFiles), [nonImages, maxFiles]);

  if (isEmpty(nonImages)) {
    return null;
  }

  return (
    <MBView row style={style}>
      <FlatList
        keyExtractor={item => item.url}
        contentContainerStyle={{ alignItems: 'center' }}
        data={filesToShow}
        horizontal={true}
        renderItem={({ item }) => (
          <FileItem
            file={item}
            editable={editable}
            showFileIcon={showFileIcon}
            onClear={() => onFileRemoved?.(item)}
            style={itemStyle}
          />
        )}
        ListFooterComponent={<FileListFooter files={nonImages} maxFiles={maxFiles} />}
      />
    </MBView>
  );
}

function FileListFooter({ files, maxFiles }: { files: FileUpload[]; maxFiles: number }) {
  const extraFilesCount = files.length - maxFiles;
  if (extraFilesCount < 1) {
    return null;
  }
  return <Pill noLeftMargin>{`+${extraFilesCount}`}</Pill>;
}
